/**
 * Tavus avatar service
 * Joins a private Tavus/Daily room and drives the replica through Echo Mode.
 */

import DailyIframe, {
  type DailyCall,
  type DailyEventObjectAppMessage,
  type DailyEventObjectParticipant,
  type DailyEventObjectParticipantLeft,
  type DailyEventObjectParticipants,
  type DailyEventObjectTrack,
  type DailyParticipant,
} from "@daily-co/daily-js";
import type { AvatarSpeechType } from "./exchangeTiming";

interface SpeechContext { exchangeId?: string; speechType: AvatarSpeechType; }
import { api } from "./api";

export interface TavusSessionConfig {
  conversationId: string;
  conversationUrl: string;
  meetingToken: string;
}

interface TavusConversationEvent {
  message_type?: "conversation";
  event_type?: string;
  conversation_id?: string;
  properties?: {
    role?: "pal" | "replica" | "user";
    modality?: "text" | "audio";
    text?: string;
    interrupted?: boolean;
    [key: string]: unknown;
  };
}

interface AvatarCallbacks {
  onStreamReady?: () => void;
  onStartTalking?: (context?: SpeechContext) => void;
  onPlaybackBlocked?: (blocked: boolean) => void;
  onStopTalking?: () => void;
  onDisconnected?: () => void;
  onError?: (error: Error) => void;
}

const DEFAULT_AVATAR_ID = "tavus-default-avatar";
const STREAM_READY_TIMEOUT_MS = 45_000;

export class TavusService {
  private playbackBlocked = false;
  private playPromise: Promise<void> | null = null;
  private pendingSpeech: Array<{ id: string; context?: SpeechContext }> = [];
  private activeSpeech: { id: string; context?: SpeechContext } | null = null;
  private call: DailyCall | null = null;
  private conversationId: string | null = null;
  private videoElement: HTMLVideoElement | null = null;
  private remoteParticipantId: string | null = null;
  private isAvatarSpeaking = false;
  private isDisconnecting = false;
  private streamReady = false;
  private callbacks: AvatarCallbacks = {};
  private streamReadyTimer: number | null = null;
  private disconnectPromise: Promise<void> | null = null;
  private lifecycleGeneration = 0;

  selectAvatarForCase(): { avatarId: string } {
    return { avatarId: DEFAULT_AVATAR_ID };
  }

  async initializeAvatar(
    sessionConfig: TavusSessionConfig,
    avatarId: string,
    callbacks: AvatarCallbacks
  ): Promise<boolean> {
    if (!sessionConfig.conversationId || !sessionConfig.conversationUrl) {
      callbacks.onError?.(new Error("Tavus session details are missing"));
      return false;
    }

    const previousCleanup = this.disconnect();
    const generation = this.lifecycleGeneration;
    await previousCleanup;
    if (generation !== this.lifecycleGeneration) {
      await api.endAvatarSession(sessionConfig.conversationId, "tavus");
      return false;
    }

    try {
      this.callbacks = callbacks;
      this.conversationId = sessionConfig.conversationId;
      this.isDisconnecting = false;
      this.streamReady = false;
      this.remoteParticipantId = null;
      void avatarId;

      this.call = DailyIframe.createCallObject({
        allowMultipleCallInstances: false,
        startAudioOff: true,
        startVideoOff: true,
        audioSource: false,
        videoSource: false,
        dailyConfig: {
          avoidEval: true,
        },
      });

      const call = this.call;
      this.bindDailyEvents(call);
      this.startStreamReadyTimeout();

      await call.join({
        url: sessionConfig.conversationUrl,
        token: sessionConfig.meetingToken,
        userName: "Patient Pro",
        startAudioOff: true,
        startVideoOff: true,
        audioSource: false,
        videoSource: false,
      });

      if (generation !== this.lifecycleGeneration) return false;
      this.syncRemoteMediaFromParticipants();
      return true;
    } catch (error: any) {
      const normalizedError =
        error instanceof Error
          ? error
          : new Error(error?.message || "Failed to initialize Tavus session");

      if (generation !== this.lifecycleGeneration) return false;
      await this.disconnect();
      callbacks.onError?.(normalizedError);
      return false;
    }
  }

  attachToElement(element: HTMLVideoElement): void {
    this.videoElement = element;
    this.videoElement.autoplay = true;
    this.videoElement.playsInline = true;
    this.syncRemoteMediaFromParticipants();
  }

  detachFromElement(element: HTMLVideoElement): void {
    if (this.videoElement !== element) return;
    element.srcObject = null;
    this.videoElement = null;
    this.playPromise = null;
  }

  isPlaybackBlocked(): boolean { return this.playbackBlocked; }

  resumePlayback(retryAbort = true): Promise<void> {
    if (this.playPromise) return this.playPromise;
    const element = this.videoElement;
    if (!element?.srcObject) return Promise.resolve();
    const generation = this.lifecycleGeneration;
    let aborted = false;
    const playing = element.play().then(() => {
      if (generation !== this.lifecycleGeneration || element !== this.videoElement) return;
      this.playbackBlocked = false;
      this.callbacks.onPlaybackBlocked?.(false);
    }).catch((error: Error) => {
      if (generation !== this.lifecycleGeneration || element !== this.videoElement) return;
      aborted = error.name === "AbortError";
      if (error.name === "NotAllowedError") {
        this.playbackBlocked = true;
        this.callbacks.onPlaybackBlocked?.(true);
      }
    }).finally(() => {
      if (this.playPromise === playing) this.playPromise = null;
      if (aborted && retryAbort && generation === this.lifecycleGeneration && element === this.videoElement)
        queueMicrotask(() => { void this.resumePlayback(false); });
    });
    this.playPromise = playing;
    return playing;
  }

  async speak(text: string, context?: SpeechContext): Promise<void> {
    if (this.isDisconnecting) {
      console.warn("Avatar is disconnecting, ignoring speak request");
      return;
    }

    if (!this.call || !this.conversationId) {
      throw new Error("Tavus session is not ready");
    }

    const cleanedText = this.cleanTextForSpeech(text);
    if (!cleanedText) {
      console.log("No speakable text after cleaning");
      return;
    }

    const id = crypto.randomUUID();
    const message: TavusConversationEvent = {
      message_type: "conversation",
      event_type: "conversation.echo",
      conversation_id: this.conversationId,
      properties: {
        modality: "text",
        text: cleanedText,
        inference_id: id,
        done: true,
      },
    };

    console.log("Sending Tavus echo message", {
      conversationId: this.conversationId,
      textLength: cleanedText.length,
    });
    this.call.sendAppMessage(message, "*");
    this.pendingSpeech.push({ id, context });
  }

  async interrupt(): Promise<void> {
    if (this.isDisconnecting || !this.call || !this.conversationId) {
      return;
    }

    const startedAt = performance.now();
    this.call.sendAppMessage(
      {
        message_type: "conversation",
        event_type: "conversation.interrupt",
        conversation_id: this.conversationId,
      },
      "*"
    );

    this.pendingSpeech = [];
    this.activeSpeech = null;
    this.isAvatarSpeaking = false;
    console.log(`Tavus interrupt sent in ${Math.round(performance.now() - startedAt)}ms`);
  }

  disconnect(): Promise<void> {
    ++this.lifecycleGeneration;
    if (this.disconnectPromise) return this.disconnectPromise;

    const call = this.call;
    const conversationId = this.conversationId;
    // Detach callbacks and media immediately; late SDK events must not revive state.
    this.forceCleanup();
    this.isDisconnecting = true;

    const localDisconnect = async () => {
      if (call && !call.isDestroyed()) {
        try {
          await call.leave();
        } finally {
          if (!call.isDestroyed()) await call.destroy();
        }
      }
    };
    // Start the keepalive request synchronously, before pagehide returns.
    const remoteEnd = conversationId
      ? api.endAvatarSession(conversationId, "tavus")
      : Promise.resolve();

    this.disconnectPromise = Promise.allSettled([localDisconnect(), remoteEnd])
      .then((results) => {
        for (const result of results) {
          if (result.status === "rejected") {
            console.warn("Tavus disconnect cleanup step failed:", result.reason);
          }
        }
      })
      .finally(() => {
        this.isDisconnecting = false;
        this.disconnectPromise = null;
      });
    return this.disconnectPromise;
  }

  isSpeaking(): boolean {
    return this.isAvatarSpeaking;
  }

  isConnected(): boolean {
    return Boolean(this.call && !this.call.isDestroyed() && !this.isDisconnecting);
  }

  canInterrupt(): boolean {
    return this.isConnected() && (this.isAvatarSpeaking || this.pendingSpeech.length > 0);
  }

  getSpeakingState(): {
    isSpeaking: boolean;
    canInterrupt: boolean;
    isConnected: boolean;
  } {
    return {
      isSpeaking: this.isAvatarSpeaking,
      canInterrupt: this.canInterrupt(),
      isConnected: this.isConnected(),
    };
  }

  private bindDailyEvents(call: DailyCall): void {
    call
      .on("joined-meeting", (event: DailyEventObjectParticipants) => {
        if (this.call !== call) return;
        this.syncRemoteMediaFromParticipants(event.participants);
      })
      .on("participant-joined", (event: DailyEventObjectParticipant) => {
        if (this.call !== call) return;
        this.handleParticipantUpdated(event.participant);
      })
      .on("participant-updated", (event: DailyEventObjectParticipant) => {
        if (this.call !== call) return;
        this.handleParticipantUpdated(event.participant);
      })
      .on("participant-left", (event: DailyEventObjectParticipantLeft) => {
        if (this.call !== call) return;
        if (event.participant.session_id === this.remoteParticipantId) {
          this.callbacks.onDisconnected?.();
          void this.disconnect();
        }
      })
      .on("track-started", (event: DailyEventObjectTrack) => {
        if (this.call !== call) return;
        if (event.participant && !event.participant.local) {
          this.handleParticipantUpdated(event.participant);
        }
      })
      .on("track-stopped", (event: DailyEventObjectTrack) => {
        if (this.call !== call) return;
        if (event.participant && event.participant.session_id === this.remoteParticipantId) {
          this.syncRemoteMediaFromParticipant(event.participant);
        }
      })
      .on("app-message", (event: DailyEventObjectAppMessage<TavusConversationEvent>) => {
        if (this.call !== call) return;
        try {
          this.handleTavusEvent(typeof event.data === "string" ? JSON.parse(event.data) : event.data);
        } catch {
          console.warn("Ignoring malformed Tavus event");
        }
      })
      .on("left-meeting", () => {
        if (this.call !== call) return;
        this.callbacks.onDisconnected?.();
        void this.disconnect();
      })
      .on("error", (event: any) => {
        if (this.call !== call) return;
        const onError = this.callbacks.onError;
        void this.disconnect();
        onError?.(new Error(event?.errorMsg || event?.message || "Daily call error"));
      });
  }

  private handleParticipantUpdated(participant: DailyParticipant): void {
    if (participant.local) {
      return;
    }

    this.remoteParticipantId = participant.session_id;
    this.syncRemoteMediaFromParticipant(participant);
  }

  private syncRemoteMediaFromParticipants(
    participants = this.call?.participants()
  ): void {
    if (!participants) {
      return;
    }

    const remoteParticipant = Object.values(participants).find(
      (participant) => participant && !participant.local
    );

    if (remoteParticipant) {
      this.handleParticipantUpdated(remoteParticipant);
    }
  }

  private syncRemoteMediaFromParticipant(participant: DailyParticipant): void {
    const videoTrack =
      participant.tracks.video.persistentTrack || participant.tracks.video.track;
    const audioTrack =
      participant.tracks.audio.persistentTrack || participant.tracks.audio.track;
    const tracks = [videoTrack, audioTrack].filter(
      (track): track is MediaStreamTrack => Boolean(track)
    );

    if (this.videoElement && tracks.length > 0) {
      const current = this.videoElement.srcObject as MediaStream | null;
      const existing = current?.getTracks() || [];
      if (tracks.length !== existing.length || tracks.some((track, i) => track !== existing[i])) {
        this.videoElement.srcObject = new MediaStream(tracks);
      }
      void this.resumePlayback();
    }

    if (!this.streamReady && videoTrack) {
      this.streamReady = true;
      this.clearStreamReadyTimeout();
      this.callbacks.onStreamReady?.();
    }
  }

  private handleTavusEvent(event: TavusConversationEvent): void {
    if (event?.message_type !== "conversation") {
      return;
    }

    const eventType = event.event_type;
    const role = event.properties?.role;
    const isPal = role === "pal" || role === "replica" || eventType?.includes("replica.");

    if (
      isPal &&
      (eventType === "conversation.started_speaking" ||
        eventType === "conversation.replica.started_speaking")
    ) {
      const id = event.properties?.inference_id;
      if (this.activeSpeech && (!id || this.activeSpeech.id === id)) return;
      const index = typeof id === "string" ? this.pendingSpeech.findIndex(item => item.id === id) : 0;
      if (index < 0 || !this.pendingSpeech.length) return;
      this.activeSpeech = this.pendingSpeech.splice(index, 1)[0];
      this.isAvatarSpeaking = true;
      this.callbacks.onStartTalking?.(this.activeSpeech.context);
      return;
    }

    if (
      isPal &&
      (eventType === "conversation.stopped_speaking" ||
        eventType === "conversation.replica.stopped_speaking")
    ) {
      const id = event.properties?.inference_id;
      if (id && this.activeSpeech?.id !== id) return;
      this.activeSpeech = null;
      this.isAvatarSpeaking = false;
      this.callbacks.onStopTalking?.();
    }
  }

  private cleanTextForSpeech(text: string): string {
    const cleaned = text
      .replace(/\*\*[^*]+\*\*/g, "")
      .replace(/\*[^*]+\*/g, "")
      .replace(
        /\([^)]*(?:speaks|says|whispers|mumbles|quietly|loudly)[^)]*\)/gi,
        ""
      )
      .replace(
        /\[[^\]]*(?:speaks|says|whispers|mumbles|quietly|loudly)[^\]]*\]/gi,
        ""
      )
      .replace(/<[^>]+>/g, "")
      .replace(/\s+/g, " ")
      .trim();

    if (cleaned !== text) {
      console.log("Text cleaned for speech:", { original: text, cleaned });
    }

    return cleaned;
  }

  private startStreamReadyTimeout(): void {
    this.clearStreamReadyTimeout();
    this.streamReadyTimer = window.setTimeout(() => {
      if (this.streamReady || this.isDisconnecting) {
        return;
      }

      this.callbacks.onError?.(
        new Error("Timed out waiting for Tavus avatar video stream")
      );
      void this.disconnect();
    }, STREAM_READY_TIMEOUT_MS);
  }

  private clearStreamReadyTimeout(): void {
    if (this.streamReadyTimer !== null) {
      window.clearTimeout(this.streamReadyTimer);
      this.streamReadyTimer = null;
    }
  }

  private forceCleanup(): void {
    this.clearStreamReadyTimeout();

    if (this.videoElement) {
      this.videoElement.srcObject = null;
    }

    this.playbackBlocked = false;
    this.playPromise = null;
    this.pendingSpeech = [];
    this.activeSpeech = null;
    this.call = null;
    this.conversationId = null;
    this.remoteParticipantId = null;
    this.isAvatarSpeaking = false;
    this.isDisconnecting = false;
    this.streamReady = false;
    this.callbacks = {};
  }
}

export const tavusService = new TavusService();
